import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { useDispatch } from "react-redux";
import useResizeListener from "../../../../hooks/useResizeListener";
import { ETypeOfNotification, Modal, setNotification } from "medere-ui-framework";
import { Room, RoomType } from "../../../../interface/interface";
import { deleteRoom } from "../../../../servicesHooks/Location/useLocationConfigService";
import { getRoomTypes } from "../../../../services/RoomTypeController";
import { useLocationConfigContext } from "../../context/LocationConfigContext";
import LocationEstablishmentFilter from "../components/LocationEstablishmentFilter";
import useEstablishmentFilterSelection from "../../hooks/useEstablishmentFilterSelection";
import CRUDBedModal from "../bedtab/CRUDBedModal";
import RoomTable from "./components/RoomTable";
import RoomTypesModal from "./components/RoomTypesModal";
import { isRoomBlockedSectorType } from "../../roomSectorRules";

export default function RoomView() {
    const dispatch = useDispatch();
    const isMobile = useResizeListener(768);
    const { establishments, orderedEstablishments, setRoomsToEstablishments, setBedsToEstablishments } = useLocationConfigContext();

    const [roomToDelete, setRoomToDelete] = useState<Room | null>(null);
    const [openRemoveModal, setOpenRemoveModal] = useState(false);
    const { selectedEstablishmentIds, setSelectedEstablishmentIds } = useEstablishmentFilterSelection(isMobile);

    const [openBedModal, setOpenBedModal] = useState(false);
    const [roomForNewBed, setRoomForNewBed] = useState<Room | null>(null);

    const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
    const [openRoomTypesModal, setOpenRoomTypesModal] = useState(false);

    const loadRoomTypes = async () => {
        try {
            setRoomTypes(await getRoomTypes());
        } catch {
            dispatch(setNotification({ title: "No se pudieron cargar los tipos de habitación", desc: "Intentá de nuevo.", icon: "pi pi-times", typeOfNotification: ETypeOfNotification.ERROR }));
        }
    };

    useEffect(() => {
        void setRoomsToEstablishments();
        void loadRoomTypes();
    }, []);

    const refreshRoomsAndBeds = async () => {
        await setRoomsToEstablishments();
        await setBedsToEstablishments();
    };

    // Las habitaciones muestran la descripción del tipo y el modal la cantidad de habitaciones por tipo
    const refreshRoomTypesAndRooms = async () => {
        await Promise.all([loadRoomTypes(), setRoomsToEstablishments()]);
    };

    const handleOpenRemoveModal = (room: Room) => {
        setRoomToDelete(room);
        setOpenRemoveModal(true);
    };

    const handleDelete = async () => {
        if (!roomToDelete || roomToDelete.room <= 0) return;
        try {
            await deleteRoom(roomToDelete.room);
            await refreshRoomsAndBeds();
            setOpenRemoveModal(false);
            setRoomToDelete(null);
            dispatch(setNotification({ title: "Eliminación exitosa", desc: "Se eliminó la habitación.", icon: "pi pi-check-circle", typeOfNotification: ETypeOfNotification.INFO }));
        } catch (error: any) {
            const status = error?.response?.status;
            if (status === 409) {
                dispatch(setNotification({ title: "No se puede eliminar", desc: "La habitación tiene dependencias asociadas.", icon: "pi pi-exclamation-triangle", typeOfNotification: ETypeOfNotification.WARNING }));
            } else {
                dispatch(setNotification({ title: "Ocurrió un error", desc: "No se pudo eliminar la habitación.", icon: "pi pi-times", typeOfNotification: ETypeOfNotification.ERROR }));
            }
        }
    };

    const openCreateBedModal = (room: Room) => {
        setRoomForNewBed(room);
        setOpenBedModal(true);
    };

    const establishmentsToRender = isMobile
        ? orderedEstablishments.filter(est => selectedEstablishmentIds.includes(est.buildingid))
        : orderedEstablishments;

    return (
        <div>
            <h1 className="title2">Habitación</h1>
            <div className="flex flex-row justify-content-between align-items-center gap-3 emr-border-bottom pb-2">
                <p className="grey-color">Las habitaciones se asocian a los sectores de establecimientos creados.</p>
                <Button
                    type="button"
                    label="Tipos de habitación"
                    icon="pi pi-list"
                    className="buttonMain2 room-types-open-btn"
                    onClick={() => setOpenRoomTypesModal(true)}
                />
            </div>

            {isMobile && (
                <LocationEstablishmentFilter
                    establishments={orderedEstablishments}
                    selectedEstablishmentIds={selectedEstablishmentIds}
                    onChange={setSelectedEstablishmentIds}
                />
            )}

            {establishmentsToRender.map((est) => {
                const hasSectors = (est.sectors ?? []).length > 0;
                const hasAllowedSectors = (est.sectors ?? []).some((sector) => !isRoomBlockedSectorType(sector.sectortype));
                return (
                    <div key={est.buildingid} className="my-5">
                        {!isMobile && (
                            <div className="flex flex-row gap-4">
                                <h2 className="title3">{est.buildingname}</h2>
                                <p className="grey-color mt-1">{est.buildingaddress}</p>
                            </div>
                        )}

                        {!hasSectors ? (
                            <p className="grey-color">No hay sectores disponibles para crear habitaciones en este establecimiento.</p>
                        ) : (
                            <>
                                {!hasAllowedSectors && (
                                    <p className="grey-color mb-3">
                                        En este establecimiento no hay sectores habilitados para crear habitaciones.
                                    </p>
                                )}
                                <RoomTable
                                    rooms={est.rooms ?? []}
                                    sectors={est.sectors ?? []}
                                    roomTypes={roomTypes}
                                    estId={est.buildingid}
                                    onDelete={handleOpenRemoveModal}
                                    onCreateBed={openCreateBedModal}
                                    onRefresh={refreshRoomsAndBeds}
                                    mobileTitle={est.buildingname}
                                    mobileSubtitle={est.buildingaddress}
                                />
                            </>
                        )}
                    </div>
                );
            })}

            <RoomTypesModal
                visible={openRoomTypesModal}
                setVisible={setOpenRoomTypesModal}
                roomTypes={roomTypes}
                onChanged={refreshRoomTypesAndRooms}
            />

            <CRUDBedModal
                visible={openBedModal}
                setVisible={setOpenBedModal}
                establishments={establishments}
                prefilledRoom={roomForNewBed}
                onSaved={refreshRoomsAndBeds}
            />

            <Modal
                className="location-mobile-editor-modal"
                visible={openRemoveModal}
                setVisible={setOpenRemoveModal}
                header={"Estás por eliminar la habitación"}
                headerStyle={{ fontSize: "12px", textAlign: "center" }}
                width="w-medium"
                closable={false}
            >
                <div>
                    <p className="grey-color">No podrás eliminar habitaciones con dependencias.</p>
                    <div style={{ marginTop: "1rem", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                        <Button label="Si, eliminar" onClick={() => { void handleDelete(); }} className="w-full buttonMain" />
                        <Button label={"Cancelar"} onClick={() => setOpenRemoveModal(false)} className="w-full btnCancel2" />
                    </div>
                </div>
            </Modal>
        </div>
    );
}
